import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import "./Avatar.css";
import {
  getDecorationFromUser,
  getAvatarDecorationBorder,
} from "../../util/avatarDecoration";

const Avatar = forwardRef(
  (
    {
      src,
      alt,
      size = 40,
      user,
      decoration,
      showBadge = false,
      onClick,
      className = "",
      style = {},
    },
    ref
  ) => {
    const resolvedDecoration = decoration || getDecorationFromUser(user);

    const sizePx =
      typeof size === "number" ? size : { sm: 32, md: 40, lg: 168 }[size] || 40;

    // Compute border from util; we also expose thickness via CSS variable
    const border = getAvatarDecorationBorder(resolvedDecoration, sizePx);

    const cssVars = useMemo(() => {
      const thickness = border ? border.split("px")[0] : 0;
      return {
        "--avatar-size": `${sizePx}px`,
        "--avatar-border": border || "none",
        "--avatar-border-thickness": `${thickness}px`,
      };
    }, [border, sizePx]);

    const fallbackSrc =
      "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Favatars%2FdefaultAvatar.png?alt=media&token=4fe7a7cf-b2a3-42bf-9d0a-067cb0fcfd72";

    return (
      <div
        ref={ref}
        className={`sl-avatar ${className} ${
          resolvedDecoration ? `decoration-${resolvedDecoration}` : ""
        }`}
        style={{ ...style, ...cssVars }}
        onClick={onClick}
        role={onClick ? "button" : undefined}
      >
        <img
          className="sl-avatar-img"
          src={src || fallbackSrc}
          alt={alt || "avatar"}
        />
        {resolvedDecoration === "diamond-overlay" && (
          <div className="sl-avatar-overlay" aria-hidden />
        )}
        {showBadge && <div className="sl-avatar-badge">⭐</div>}
      </div>
    );
  }
);

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.object,
  decoration: PropTypes.string,
  showBadge: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default Avatar;
